import React, { useEffect, useState } from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { UPSERT_ASSET_FORM_ID } from '../../components/UpsertAssetModal/UpsertAssetModal.meta';
import { PensionDTO, PensionPlanTypeEnum } from '../../tables/Pensions/Pensions.meta';
import { usePensionForm } from './usePensionForm';
import {
  DISTRIBUTION_AGE_WARNING,
  LUMP_SUM_WARNING,
  MONTHLY_PAYMENT_WARNING,
  PensionFormValues,
} from './PensionForm.meta';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Radio } from '../../../../CommonComponents/OPL/Radio/Radio';
import { Label } from '../../../../CommonComponents/OPL/Label/Label';
import { Toggle } from '../../../../CommonComponents/OPL/Toggle/Toggle';
import { OtherAssetsDTO } from '../../api/useOtherAssetsQuery';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { GrossEstateCheckbox } from '../components/GrossEstateCheckbox/GrossEstateCheckbox';
import { ManagementStyle } from '../components/ManagementStyle/ManagementStyle';
import { AssetBucketSelect } from '../components/AssetBucketSelect/AssetBucketSelect';
import { AssetOwnership } from '../components/AssetOwnership/AssetOwnership';
import { APPEND_ASSET_OWNER, OwnerModelFormDTO } from '../../OtherAssets.meta';

export interface Props {
  clientId: string;
  asset: PensionDTO | null;
  options: OtherAssetsDTO['Options'];
  onSuccess: () => void;
  isEstatePlanningWorkflow: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

export const PensionForm = ({
  clientId,
  options,
  asset,
  onSuccess,
  isEstatePlanningWorkflow,
  onDirtyChange,
}: Props) => {
  const {
    data: {
      formBag: {
        register,
        control,
        watch,
        formState: { errors, isDirty },
        setValue,
        resetField,
      },
      mutationBag,
      distributionAgeOptions,
      showSyncPlanValue,
      assetBucketingByManagementStyle,
      managementStylesOptions,
      onManagementSelect,
      assetBucketOptions,
      managementStyleSelection,
      allowAssetBucketingCustomization,
      displayAssetBucketing,
    },
    actions: { onSubmit, resetValues },
  } = usePensionForm({ clientId, options, asset, onSuccess });

  const [showLumpSumWarning, setShowLumpSumWarning] = useState(false);
  const [showMonthlyPaymentWarning, setShowMonthlyPaymentWarning] = useState(false);
  const [showDistributionAgeWarning, setShowDistributionAgeWarning] = useState(false);

  const watchedType = watch('type');
  const watchedIsInflationAdjusted = watch('isInflationAdjusted');

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    resetValues();
  }, [asset]);

  const { fields, update, append, remove } = useFieldArray<
    PensionFormValues,
    'ownerModel',
    'fieldKey'
  >({
    control,
    name: 'ownerModel',
    keyName: 'fieldKey',
  });

  const watchOwner = useWatch({ control, name: 'ownerModel' });

  const ownerModelFields = fields.map((field, index) => ({
    ...field,
    ...watchOwner[index],
  }));

  const updateOwner = (index: number, data: OwnerModelFormDTO) => {
    update(index, {
      ...ownerModelFields[index],
      ...data,
    });
  };

  const setPercentage = (percentArr: number[]) => {
    ownerModelFields.map((field, index) => {
      setValue(`ownerModel.${index}.Percentage`, percentArr[index].toString());
    });
  };

  return (
    <form
      onSubmit={onSubmit}
      id={UPSERT_ASSET_FORM_ID}
      css={`
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.space['16']}px;
      `}
      aria-label="Pension form"
    >
      {mutationBag.error && (
        <Alert
          variant="error"
          message={mutationBag.error.message}
          onRequestClose={mutationBag.reset}
        />
      )}

      <Input
        label="Name"
        maxLength={32}
        {...register('name', {
          onBlur: evt => {
            if (evt.target.value !== asset?.Name) {
              setValue('isSyncAssetValueEnabled', false);
            }
          },
        })}
      />

      <AssetOwnership<PensionFormValues>
        control={control}
        options={options.PensionOptions.recepientOptions}
        allowsUnequalOwnership={options.PensionOptions.AllowUnequalOwnership}
        ownerModelFields={ownerModelFields}
        updateOwner={updateOwner}
        appendOwner={() => append({ ...APPEND_ASSET_OWNER })}
        removeOwner={(index: number) => remove(index)}
        setPercentage={setPercentage}
        label="Recipient"
      />

      <Controller
        name="type"
        control={control}
        render={({ field: { value, onChange, ref } }) => (
          <Select
            innerRef={ref}
            label="Type"
            placeholder="Select type"
            value={options.PensionOptions.planTypeOptions.find(option => option.value === value)}
            options={options.PensionOptions.planTypeOptions}
            onChange={option => {
              onChange(option?.value);
              if (option?.value === PensionPlanTypeEnum.LumpSum) {
                setValue('isLiquid', true);
                setValue('isInflationAdjusted', false, { shouldValidate: true });
                setValue('inflation', null, { shouldValidate: true });
              } else {
                resetField('isLiquid');
              }
            }}
            formVariant={errors.type && 'error'}
            helperText={errors.type?.message}
            isPortal
          />
        )}
      />

      {watchedType === PensionPlanTypeEnum.LumpSum && (
        <Controller
          control={control}
          name="lumpSumAmount"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Lump Sum Amount"
              decimalScale={2}
              allowNegative={false}
              leftIcon="far fa-dollar-sign"
              helperText={
                errors.lumpSumAmount?.message || (showLumpSumWarning ? LUMP_SUM_WARNING : undefined)
              }
              formVariant={
                errors.lumpSumAmount ? 'error' : showLumpSumWarning ? 'warning' : undefined
              }
              onValueChange={({ floatValue }) => {
                onChange(floatValue ?? null);

                if (asset && showSyncPlanValue) {
                  setShowLumpSumWarning(true);
                  setValue('isSyncAssetValueEnabled', false);
                }
              }}
              {...field}
            />
          )}
        />
      )}

      {watchedType !== PensionPlanTypeEnum.LumpSum && (
        <Controller
          control={control}
          name="monthlyPayment"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Monthly Payment"
              decimalScale={2}
              allowNegative={false}
              leftIcon="far fa-dollar-sign"
              innerText="/month"
              helperText={
                errors.monthlyPayment?.message ||
                (showMonthlyPaymentWarning ? MONTHLY_PAYMENT_WARNING : undefined)
              }
              formVariant={
                errors.monthlyPayment ? 'error' : showMonthlyPaymentWarning ? 'warning' : undefined
              }
              onValueChange={({ floatValue }) => {
                onChange(floatValue ?? null);

                if (asset && showSyncPlanValue) {
                  setShowMonthlyPaymentWarning(true);
                  setValue('isSyncAssetValueEnabled', false);
                }
              }}
              {...field}
            />
          )}
        />
      )}

      <Controller
        name="distributionAge"
        control={control}
        render={({ field: { value, onChange, ref } }) => (
          <Select
            innerRef={ref}
            label={watchedType === PensionPlanTypeEnum.LumpSum ? 'Payment Age' : 'Distribution Age'}
            value={distributionAgeOptions.find(option => option.value === value)}
            options={distributionAgeOptions}
            helperText={showDistributionAgeWarning ? DISTRIBUTION_AGE_WARNING : undefined}
            formVariant={showDistributionAgeWarning ? 'warning' : undefined}
            onChange={option => {
              onChange(option?.value);

              if (asset && showSyncPlanValue) {
                setShowDistributionAgeWarning(true);
                setValue('isSyncAssetValueEnabled', false);
              }
            }}
            isPortal
          />
        )}
      />

      {isEstatePlanningWorkflow && watchedType !== PensionPlanTypeEnum.SingleLife && (
        <GrossEstateCheckbox control={control} />
      )}

      {isEstatePlanningWorkflow && watchedType === PensionPlanTypeEnum.LumpSum && (
        <Box>
          <Text as="label" variant="formLabel" color="neutral.600">
            Liquidity Status
          </Text>

          <Box display="flex" flexDirection="column" gridGap="8" mb="8">
            <Box>
              <Controller
                name="isLiquid"
                control={control}
                render={({ field: { value, name, onChange } }) => (
                  <>
                    <Radio name={name} checked={value} onChange={() => onChange(true)}>
                      Liquid
                    </Radio>
                    <Radio name={name} checked={!value} onChange={() => onChange(false)} mr="4">
                      Illiquid
                    </Radio>
                  </>
                )}
              />
            </Box>
          </Box>
        </Box>
      )}

      {watchedType !== null && watchedType !== PensionPlanTypeEnum.LumpSum && (
        <>
          <Controller
            control={control}
            name="isInflationAdjusted"
            render={({ field: { value, onChange } }) => {
              return (
                <Box role="radiogroup" aria-labelledby="is-inflation-adjusted--label">
                  <Label id="is-inflation-adjusted--label" mb="4">
                    Inflation Adjusted
                  </Label>

                  <Box display="flex">
                    <Radio
                      name="isInflationAdjusted"
                      checked={value}
                      onChange={() => onChange(true)}
                      mr="4"
                    >
                      Yes
                    </Radio>

                    <Radio
                      name="isInflationAdjusted"
                      checked={!value}
                      onChange={() => onChange(false)}
                    >
                      No
                    </Radio>
                  </Box>
                </Box>
              );
            }}
          />

          {watchedIsInflationAdjusted && (
            <Controller
              control={control}
              name="inflation"
              render={({ field: { onChange, ...field } }) => (
                <InputNumberMask
                  label="Inflation Rate"
                  rightIcon="far fa-percent"
                  decimalScale={2}
                  allowNegative={false}
                  onValueChange={({ floatValue }) => {
                    onChange(floatValue ?? null);
                  }}
                  {...field}
                />
              )}
            />
          )}
        </>
      )}

      {watchedType === PensionPlanTypeEnum.JointSurvivor && (
        <Controller
          control={control}
          name="survivorBenefit"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Survivor Benefit"
              rightIcon="far fa-percent"
              decimalScale={2}
              allowNegative={false}
              onValueChange={({ floatValue }) => {
                onChange(floatValue ?? null);
              }}
              {...field}
            />
          )}
        />
      )}

      {assetBucketingByManagementStyle && (
        <ManagementStyle<PensionFormValues>
          control={control}
          options={managementStylesOptions}
          formKey="orionManagementStyleId"
          onManagementSelect={onManagementSelect}
        />
      )}

      {displayAssetBucketing && (
        <AssetBucketSelect<PensionFormValues>
          control={control}
          formKey="customBucketIndex"
          isDisabled={
            (assetBucketingByManagementStyle && !allowAssetBucketingCustomization) ||
            (assetBucketingByManagementStyle && managementStyleSelection === null)
          }
          options={assetBucketOptions}
        />
      )}

      {showSyncPlanValue && (
        <Toggle {...register('isSyncAssetValueEnabled')} size="small">
          Sync Plan Value
        </Toggle>
      )}
    </form>
  );
};
